// The diff reader's commands (the Files view): what each command means
// with this file open, this cursor and this selection.
//
// `updateReader(state, env, msg)` is pure. `ReaderState` is the reader's
// own model: the line cursor, the range, the selection, the thread cursor.
// It is whatever the last update returned, and changes no other way: a
// command, an answer to an effect and what the pointer did (`PointerMsg`)
// are all messages. `ReaderEnv` is what the reader is shown and does not own
// (the file, its changes and their review, the folds, the preferences),
// read when the message is sent. The cursor's change is one of those: it
// is kept with the pull request's preferences, so an update moves it by
// the `cursor` effect and never by returning it. Work that needs another
// file's diff or the page's geometry is an effect (`seek`) whose answer
// comes back as a message (`arrived`, `entered`, `nowhere`).
//
// Verbs act on the selection, else on the object under the cursor: a line
// range, or an object selected whole (a change, the file), then the
// cursor's change or line.

import type { Block, Scope } from '../engine/types';
import { gutterPress } from '../logic/gutterDrag';
import { commentLine, lineStopAfter, queueEnd, rangeEndAt, skipsFile, type FileProgress } from '../logic/reader';
import { blockLines, blockReviewed, firstUncovered, reviewedNote, type Coverage } from '../logic/partial';
import { changedLine } from '../review/model';
import { expandSelection, foldable, levelScopes, scopeId, scopeUnder, sideRange, type LineSpan } from '../logic/scopeFold';
import { anchorKey, stopIndex, type LineCursor, type LineStop } from '../state/anchor';
import { times, type Command, type Dir, type Only, type Ref } from './command';
import { announce, notApplicable, nothingFor, pass, type Shared, type Update } from './effects';

type Side = 'LEFT' | 'RIGHT';

/** A change block of the open file, as the commands need it. */
export interface ChangeInfo {
  key: string;
  /** None of its removed and added lines is unreviewed (`isUnreviewed`): what n / p pass over, and what `r` has nothing to mark in. */
  reviewed: boolean;
  folded: boolean;
  /** Its removed and added lines no mark covers, in row order. */
  uncovered: readonly { row: number; at: LineCursor }[];
  /** How many of its removed and added lines marks cover. */
  covered: number;
  /** Its first removed or added line. */
  first: LineCursor | null;
  /** The line a comment goes on with no line cursor: its first added line, else its first removed one. */
  commentAt: LineCursor | null;
  /** The lines it shows on each side. */
  lines: Readonly<Record<Side, LineSpan | null>>;
}

/**
 * A change block as the commands need it, under the marks' `coverage` of
 * its rows (null while that is not known: no line counts as covered).
 * Its `reviewed`, the lines n lands on and what `r` marks all come from
 * that one coverage, so they agree.
 */
export function changeInfo(block: Block, coverage: Coverage | null, folded: boolean): ChangeInfo {
  const uncovered: ChangeInfo['uncovered'][number][] = [];
  block.rows.forEach((r, row) => {
    const l = changedLine(r);
    if (l && !coverage?.covered[row]) uncovered.push({ row, at: { side: l.side === 'old' ? 'LEFT' : 'RIGHT', line: l.line } });
  });
  return {
    key: block.key,
    reviewed: blockReviewed(block, coverage),
    folded,
    uncovered,
    covered: blockLines(block, coverage).done,
    first: firstUncovered(block, null),
    commentAt: commentLine(block),
    lines: { LEFT: sideRange(block.rows, 'LEFT'), RIGHT: sideRange(block.rows, 'RIGHT') },
  };
}

/** A thread shown in the open file, in reading order. */
export interface ThreadInfo {
  id: string;
  /** The block it sits in; null for a note above the diff. */
  block: string | null;
  resolved: boolean;
}

/** The selected lines, on one side of the open file. */
export interface Lines {
  side: Side;
  start: number;
  end: number;
}

/** One displayed row of the open file, in display order: the lines it shows, and its changed line's review. */
export interface RowInfo {
  /** The block it is in, and its place among that block's rows. */
  block: string;
  row: number;
  /** The line it shows on each side: a removed row has no new line, an added row no old one. */
  old: number | null;
  new: number | null;
  /** The side its changed line is marked on (removed rows on the old, added rows on the new); null for a row that changes nothing. */
  mark: Side | null;
  /** A review mark covers its changed line. */
  covered: boolean;
}

/**
 * A collapsed fold (a folded scope): one row, its head line's, standing for
 * the rows it hides. `row`, `from` and `to` index the file's rows
 * (`ReaderEnv.rows`); `row` is -1 when the head line is not among them. A
 * fold inside another collapsed fold is not listed: the outer one hides it.
 */
export interface FoldInfo {
  /** The scope's `scopeId`. */
  id: string;
  title: string;
  /** The head line, on the new side. */
  head: number;
  row: number;
  from: number;
  to: number;
}

/**
 * What a selection of lines stands for: the lines themselves, through
 * every collapsed fold whose row is among them, and the changed lines
 * that takes in.
 */
export interface RangeInfo {
  /** The selection's side, and its first and last line there once the folds' hidden lines are counted in. */
  through: Lines;
  /** Every removed and added line it reaches, in display order: what marking or unmarking writes. */
  marks: readonly LineCursor[];
  /** How many of them marks cover. */
  covered: number;
  /** The change blocks it reaches, in order, with the row its span ends at in each, and whether it leaves any of the block's lines to mark, or any marked. */
  spans: readonly { block: string; end: number; unmarked: number; marked: number }[];
  /** The collapsed folds whose rows it includes. */
  folds: readonly FoldInfo[];
}

/** One step of growing the selection: what was selected before. */
export interface Grown {
  side: Side;
  span: LineSpan;
}

/** The reader's own model. */
export interface ReaderState {
  lineCursor: LineCursor | null;
  /** Where the range being extended started; null without one. */
  rangeAnchor: LineCursor | null;
  /** The selected lines, however they were selected: a range, a drag or shift-click, a link's lines, a selected object's lines. */
  selection: Lines | null;
  /** A pointer drag is still choosing where the selection ends: until it ends, nothing is selected for a verb. */
  dragging: boolean;
  /** The object the selection stands for, when one was selected whole. */
  selected: Ref | null;
  /** The thread the thread motions last went to. */
  threadCursor: string | null;
  /** The selections grown from, innermost last, and the selection they led to. */
  grown: { stack: readonly Grown[]; to: Grown | null };
}

/** The reader with a file just opened: nothing selected, no cursors. */
export const READER_START: ReaderState = { lineCursor: null, rangeAnchor: null, selection: null, dragging: false, selected: null, threadCursor: null, grown: { stack: [], to: null } };

/** What the reader is shown and does not own, as it is when a message is sent. */
export interface ReaderEnv {
  /** The open file. */
  file: string | null;
  /** The changed files the filters leave, in the file list's order, with their review progress. */
  files: readonly { path: string; progress: FileProgress }[];
  /** Every file the list shows, in its order. */
  visible: readonly string[];
  /** The rendered preview is shown instead of the source: lines take nothing. */
  preview: 'markdown' | 'image' | null;
  /** The file has a rendered form. */
  previewable: boolean;
  changes: readonly ChangeInfo[];
  /** The index of the cursor's change; -1 with none. */
  cursor: number;
  /** Every block of the file, in display order. */
  blocks: readonly string[];
  /** The blocks that are folded. */
  folded: ReadonlySet<string>;
  /** The block each line sits in, by anchor (`anchorKey`). */
  blockOf: ReadonlyMap<string, string>;
  /** The removed and added lines no mark covers, by anchor. */
  unreviewed: ReadonlySet<string>;
  /** The lines the line cursor can rest on. */
  stops: readonly LineStop[];
  /** The new-side line at the line cursor, else at the top of the reader. */
  newLine: number | null;
  /** Every row of the file, in display order, folded away or not. */
  rows: readonly RowInfo[];
  /** The collapsed folds, in order. */
  folds: readonly FoldInfo[];
  /** The marks' coverage of the file is known. */
  marksReady: boolean;
  /** The file's scopes on each side. */
  scopes: Readonly<Record<Side, readonly Scope[]>>;
  /** The folded scopes, by `scopeId`. */
  foldedScopes: ReadonlySet<string>;
  threads: readonly ThreadInfo[];
  /** A comment is being written on the selected lines: they stay selected while it is. */
  composing: boolean;
  /** Why review marks cannot be taken here; null while they can. */
  marksOff: string | null;
  /** Nothing can be written here. */
  readOnly: boolean;
  wrap: boolean;
  layout: 'split' | 'unified';
  fileListHidden: boolean;
  algorithm: string;
  algorithms: readonly string[];
}

/** The two together, as the update works on them. Within one update the cursor may be moved to work out what follows; only the state comes back. */
export type ReaderModel = Omit<ReaderState, 'dragging'> & ReaderEnv;

/** What is marked or unmarked. */
export type MarkTarget = { kind: 'change'; key: string } | { kind: 'lines'; lines: readonly LineCursor[] } | { kind: 'file' };

/** A motion that leaves the file, or starts from what the reader shows. */
export type Seek =
  | { object: 'change'; dir: Dir; only?: Only; count: number; sections: boolean }
  /** `from`: the thread nearest the top of the reader (`view`), or the next file's (`file`). */
  | { object: 'thread'; dir: Dir; only?: Only; count: number; from: 'view' | 'file' };

export type ReaderEffect =
  | { kind: 'scroll'; dx: number; dy: number; unit: 'px' | 'page' }
  | {
      kind: 'scroll-to';
      to: { kind: 'top' } | { kind: 'end' } | { kind: 'change'; key: string } | { kind: 'thread'; id: string } | { kind: 'line'; at: LineCursor };
      /**
       * Where it comes to rest: at the top of the reader, only as far as
       * needed, or in the middle; `fit`: all of it in view, in the middle,
       * when it fits, else at the top; `reveal`: at the top unless it is in
       * view already.
       */
      how: 'top' | 'nearest' | 'centre' | 'fit' | 'reveal';
      /** For a line: also the folded-lines stub right above it. */
      withStub?: true;
    }
  | { kind: 'focus'; what: 'reader' | 'finder' }
  /** The focus leaves the selection's floating bar for the reader. */
  | { kind: 'leave-bar' }
  /** Make this change the cursor's. */
  | { kind: 'cursor'; key: string }
  | { kind: 'set-fold'; key: string; value: boolean }
  | { kind: 'fold-scopes'; ids: readonly string[]; value: boolean }
  | { kind: 'open-file'; path: string }
  /** Find where a motion lands; the answer comes back as a message. */
  | { kind: 'seek'; seek: Seek }
  /** Write review marks; `then` is sent once they are written. */
  | { kind: 'write-marks'; target: MarkTarget; value: boolean; then: ReaderMsg | null }
  | { kind: 'open-composer'; side: Side; from: number; to: number; suggest: boolean }
  | { kind: 'close-composer' }
  /** Copy a link to the file, or to lines of it. */
  | { kind: 'copy-link'; lines: Lines | null }
  /** Shown beside the move buttons; `passing` notes clear by themselves. Null clears it. */
  | { kind: 'note'; text: string | null; passing?: true }
  /** Say why nothing can be written here. */
  | { kind: 'read-only' }
  | { kind: 'set-preview'; on: boolean }
  | { kind: 'set-file-list'; hidden: boolean }
  | { kind: 'set-pref'; pref: 'wrap'; value: boolean }
  | { kind: 'set-pref'; pref: 'layout'; value: 'split' | 'unified' }
  | { kind: 'set-pref'; pref: 'algorithm'; value: string };

/** A command, or the answer to an effect of an earlier one. */
export type ReaderMsg =
  | Command
  /** A seek found this change (the file it is in is open now). */
  | { kind: 'arrived'; key: string; dir: Dir; wrapped: 'change' | 'file' | null; only?: Only }
  /** A seek through a preview's changed sections ran out of them: the motion goes on by change. */
  | { kind: 'past-sections'; dir: Dir; only?: Only; count: number }
  /** A seek found this thread in the open file; `rest` more steps follow. */
  | { kind: 'found-thread'; id: string; dir: Dir; only?: Only; rest: number }
  /** A seek opened another file: the motion goes on from its edge. */
  | { kind: 'entered'; dir: Dir; only?: Only; count: number }
  /** A seek found nothing. */
  | { kind: 'nowhere'; seek: Seek }
  /**
   * Lines were marked or unmarked: `lines` of them changed, in `changes`
   * change blocks; `fold` names the collapsed fold they were marked
   * through, when it was one.
   */
  | { kind: 'marked'; value: boolean; lines: number; changes: number; fold: string | null; spans: readonly { block: string; end: number }[]; onward: boolean }
  /** A definition was chosen to go to (its name in the scope crumb, or the chip on its last line): the line cursor lands on its head, as `[ s` would. */
  | { kind: 'scope-chosen'; side: Side; scope: Scope }
  | PointerMsg;

/**
 * What the pointer, a link and the page tell the reader, each by name:
 * the model changes for them in `update`, as it does for commands. Lines
 * selected this way stand for no object.
 */
export type PointerMsg =
  /** Another file is open: the model starts over. */
  | { kind: 'file-opened' }
  /** Another comparison of the same file: what named the old one's lines goes. */
  | { kind: 'comparison-changed' }
  /** A press on a line number (see `gutterPress`): it selects, extends with `shift`, or clears, and may start a drag. */
  | { kind: 'gutter-pressed'; at: LineCursor; shift: boolean }
  /** The drag under way reached this line. */
  | { kind: 'drag-moved'; line: number }
  /** The drag under way ended: one line without `shift` is a click, which comments on it; anything else is a range. */
  | { kind: 'drag-ended'; shift: boolean }
  /** The drag's end will not be seen (the window lost the focus): its lines stay selected. */
  | { kind: 'drag-abandoned' }
  /** These lines are shown as the selection: a link's, or those a comment box opened on. */
  | { kind: 'lines-shown'; lines: Lines }
  /** The selection goes (a comment box closed, a change was clicked), and with it the range being extended unless `keepRange`. */
  | { kind: 'selection-dropped'; keepRange?: boolean }
  /** The reader was scrolled until the cursor's change was out of sight, and this change is the first that shows (`cursorInView`). */
  | { kind: 'view-moved'; key: string };

type Step = Update<ReaderModel, ReaderEffect>;
type Fx = (ReaderEffect | Shared)[];

const sideName = (side: Side) => (side === 'LEFT' ? 'old' : 'new');
const nLines = (n: number) => `${n} line${n === 1 ? '' : 's'}`;
const sameAt = (a: LineCursor | null, b: LineCursor | null) => !!a && !!b && a.side === b.side && a.line === b.line;
const HERE = 'in this file';

const PREVIEW_ONLY = notApplicable('The preview is read-only: show the source to work on its lines', { kind: 'present', what: 'preview' });
const NO_FILE = notApplicable('No file is open');

const others = (m: ReaderModel) => m.files.filter((f) => f.path !== m.file).map((f) => f.progress);
const reviewedFlags = (m: ReaderModel) => m.changes.map((c) => c.reviewed);

/** The selection `m.selected` still stands for: an object selected whole is one only while its lines are the selection. */
function selectedObject(m: ReaderModel): Ref | null {
  if (!m.selected) return null;
  if (m.selected.object === 'file') return m.selection === null && m.selected.id === m.file ? m.selected : null;
  return m.selection ? m.selected : null;
}

const cleared = (m: ReaderModel): ReaderModel => ({ ...m, rangeAnchor: null, selection: null, selected: null });

// ---------------------------------------------------------------- what a row stands for

/** The line a row shows on `side`. */
const lineOn = (r: RowInfo, side: Side): number | null => (side === 'LEFT' ? (r.mark !== 'RIGHT' ? r.old : null) : r.mark !== 'LEFT' ? r.new : null);

/**
 * The lines a selection stands for. A row that is a collapsed fold's
 * stands for every line the fold hides, so a selection that includes such
 * a row reaches through the fold to its last hidden row, exactly as if
 * those lines were shown and selected; a fold inside it is covered with
 * it. Null when no row shows a selected line.
 */
export function standsFor(env: Pick<ReaderEnv, 'rows' | 'folds'>, lines: Lines): RangeInfo | null {
  const lo = Math.min(lines.start, lines.end);
  const hi = Math.max(lines.start, lines.end);
  const { rows } = env;
  // In each block, the rows from the first that shows a selected line to the last, with the other side's rows
  // between them (a replacement's removed lines sit between its added ones' neighbours): as `selectionSpans` has it.
  const taken = new Array<boolean>(rows.length).fill(false);
  let any = false;
  for (let i = 0; i < rows.length; ) {
    let j = i;
    let first = -1;
    let last = -1;
    for (; j < rows.length && rows[j].block === rows[i].block; j++) {
      const n = lineOn(rows[j], lines.side);
      if (n === null || n < lo || n > hi) continue;
      if (first < 0) first = j;
      last = j;
    }
    for (let k = first; first >= 0 && k <= last; k++) taken[k] = any = true;
    i = j;
  }
  if (!any) return null;
  // A fold whose row is taken brings every row it hides.
  const folds = env.folds.filter((f) => f.row >= 0 && taken[f.row]);
  for (const f of folds) for (let k = f.from; k <= f.to; k++) taken[k] = true;
  const marks: LineCursor[] = [];
  const spans: { block: string; end: number; unmarked: number; marked: number }[] = [];
  let covered = 0;
  let end = hi;
  rows.forEach((r, i) => {
    if (!taken[i]) return;
    const n = lineOn(r, lines.side);
    if (n !== null && n > end) end = n;
    if (!r.mark) return;
    marks.push({ side: r.mark, line: r.mark === 'LEFT' ? r.old! : r.new! });
    if (r.covered) covered++;
    const span = spans[spans.length - 1]?.block === r.block ? spans[spans.length - 1] : spans[spans.push({ block: r.block, end: 0, unmarked: 0, marked: 0 }) - 1];
    span.end = r.row + 1;
    if (r.covered) span.marked++;
    else span.unmarked++;
  });
  return { through: { side: lines.side, start: lo, end }, marks, covered, spans, folds };
}

/** The collapsed fold whose row the line cursor is on, if it is on one. */
const foldAt = (m: Pick<ReaderEnv, 'folds'>, at: LineCursor | null): FoldInfo | null => (at?.side === 'RIGHT' ? (m.folds.find((f) => f.row >= 0 && f.head === at.line) ?? null) : null);

/** A collapsed fold's changed lines, its own row's included: how many, how many reviewed, in how many changes. What its row shows of its review. */
export function foldReview(rows: readonly RowInfo[], fold: FoldInfo): { done: number; total: number; changes: number } {
  let done = 0;
  let total = 0;
  const blocks = new Set<string>();
  for (let i = fold.row >= 0 ? fold.row : fold.from; i <= fold.to; i++) {
    const r = rows[i];
    if (!r?.mark) continue;
    total++;
    if (r.covered) done++;
    blocks.add(r.block);
  }
  return { done, total, changes: blocks.size };
}

/**
 * Where the line cursor rests for line `at`: on the line, or, when a
 * collapsed fold hides it, on that fold's row. The line cursor never rests
 * inside a folded body.
 */
function restAt(m: Pick<ReaderEnv, 'rows' | 'folds'>, at: LineCursor): { at: LineCursor; fold: FoldInfo | null } {
  if (!m.folds.length) return { at, fold: null };
  const i = m.rows.findIndex((r) => r.mark === at.side && (at.side === 'LEFT' ? r.old : r.new) === at.line);
  const hit = i < 0 ? m.rows.findIndex((r) => lineOn(r, at.side) === at.line) : i;
  const fold = hit < 0 ? undefined : m.folds.find((f) => f.row >= 0 && f.from <= hit && hit <= f.to);
  return fold ? { at: { side: 'RIGHT', line: fold.head }, fold } : { at, fold: null };
}

const nChanges = (n: number) => `${n} change${n === 1 ? '' : 's'}`;

// ---------------------------------------------------------------- changes

/**
 * The cursor on change `i`: it is unfolded and scrolled to, and a range
 * left in another block is dropped (it would still be what a verb acts
 * on). With `only`, the line cursor goes to the change's first unreviewed
 * line, so the next range starts there.
 */
function landOnChange(m: ReaderModel, i: number, only: Only | undefined, said?: (where: string | null) => string): Step {
  const c = m.changes[i];
  let next: ReaderModel = { ...m, cursor: i };
  const fx: Fx = [{ kind: 'note', text: null }];
  if (m.selection && !m.composing && !standsFor(m, m.selection)?.spans.some((s) => s.block === c.key)) next = cleared(next);
  fx.push({ kind: 'cursor', key: c.key }, { kind: 'set-fold', key: c.key, value: false }, { kind: 'scroll-to', to: { kind: 'change', key: c.key }, how: 'fit' }, { kind: 'focus', what: 'reader' });
  let where: string | null = null;
  const line = only ? (c.uncovered[0]?.at ?? null) : null;
  if (line) {
    // A line a collapsed fold hides is reached at the fold's row, which stands for it.
    const { at, fold } = restAt(m, line);
    next = { ...next, lineCursor: at };
    if (next.selection && !m.composing) next = cleared(next);
    where = fold ? `Line cursor on the folded ${fold.title}; ${nLines(c.uncovered.length)} unreviewed in the change` : `Line cursor on ${sideName(at.side)} line ${at.line}; ${nLines(c.uncovered.length)} unreviewed in it`;
    // Below the change's first changed line, that line is brought to the top when the change did not fit in view.
    if (!sameAt(at, c.first)) fx.push({ kind: 'scroll-to', to: { kind: 'line', at }, how: 'reveal' });
  }
  const here = `Change ${i + 1} of ${m.changes.length}${c.reviewed ? ', reviewed' : ', not reviewed'}`;
  fx.push(announce(said ? said(where) : where ? `${here}. ${where}` : here));
  return [next, fx];
}

/** The indices of the changes a motion stops at from `from`, nearest first. */
function changesAhead(m: ReaderModel, from: number, dir: Dir, only: Only | undefined): number[] {
  const out: number[] = [];
  for (let i = from < 0 ? (dir > 0 ? 0 : m.changes.length - 1) : from + dir; i >= 0 && i < m.changes.length; i += dir) if (!only || !m.changes[i].reviewed) out.push(i);
  return out;
}

function moveChange(m: ReaderModel, dir: Dir, only: Only | undefined, count: number, sections = m.preview === 'markdown'): Step {
  if (!m.file) return [m, [NO_FILE]];
  // A rendered document is read by its changed sections first, which only the page can find.
  const ahead = sections ? [] : changesAhead(m, m.cursor, dir, only);
  if (ahead.length >= count) return landOnChange(m, ahead[count - 1], only);
  // Past the file's last (or first) change the motion goes on in the changed files around it, and wraps.
  return [m, [{ kind: 'note', text: null }, { kind: 'seek', seek: { object: 'change', dir, only, count: count - ahead.length, sections } }]];
}

// ---------------------------------------------------------------- lines

/** The line cursor on stop `i`: the change it is in becomes the cursor's, and a range being extended reaches it. */
function landOnStop(m: ReaderModel, i: number): Step {
  const stop = m.stops[i];
  const at: LineCursor = { side: stop.side, line: stop.line };
  let next: ReaderModel = { ...m, lineCursor: at };
  const fx: Fx = [];
  const change = m.changes.findIndex((c) => c.key === stop.block);
  if (change >= 0 && change !== m.cursor) {
    next = { ...next, cursor: change };
    fx.push({ kind: 'cursor', key: stop.block });
  }
  let note = '';
  if (m.rangeAnchor) {
    const end = rangeEndAt(m.stops as LineStop[], m.rangeAnchor, i);
    if (end !== null) {
      next = { ...next, selection: { side: m.rangeAnchor.side, start: m.rangeAnchor.line, end }, selected: null };
      const lo = Math.min(m.rangeAnchor.line, end);
      const hi = Math.max(m.rangeAnchor.line, end);
      note = lo === hi ? `, selecting ${sideName(m.rangeAnchor.side)} line ${lo}` : `, selecting ${sideName(m.rangeAnchor.side)} lines ${lo} to ${hi}`;
    }
  }
  fx.push(announce(`Line ${stop.line}, ${sideName(stop.side)} side${note}`), { kind: 'scroll-to', to: { kind: 'line', at }, how: 'nearest' });
  return [next, fx];
}

function moveLine(m: ReaderModel, command: Extract<Command, { kind: 'move' }>): Step {
  if (!m.file) return [m, [NO_FILE]];
  if (m.preview) return [m, [PREVIEW_ONLY]];
  if (!m.stops.length) return [m, [nothingFor(command, HERE)]];
  const stops = m.stops as LineStop[];
  const count = times(command);
  if (!command.only) return landOnStop(m, lineStopAfter(stops, m.lineCursor, m.changes[m.cursor]?.key ?? null, command.dir * count, m.blocks));
  const from = stopIndex(stops, m.lineCursor);
  let left = count;
  let last = -1;
  for (let i = from < 0 ? (command.dir > 0 ? 0 : stops.length - 1) : from + command.dir; i >= 0 && i < stops.length && left > 0; i += command.dir) {
    // A fold's row counts while a line it hides is unreviewed.
    const fold = foldAt(m, stops[i]);
    if (!m.unreviewed.has(anchorKey(stops[i].side, stops[i].line)) && !(fold && foldReview(m.rows, fold).done < foldReview(m.rows, fold).total)) continue;
    last = i;
    left--;
  }
  return last < 0 ? [m, [notApplicable(`No unreviewed line ${command.dir > 0 ? 'after' : 'before'} the line cursor in this file`)]] : landOnStop(m, last);
}

/** The line cursor on a line that may be folded away: its block is unfolded first. */
function landOnLine(m: ReaderModel, at: LineCursor, said: string): Step {
  const block = m.blockOf.get(anchorKey(at.side, at.line));
  const fx: Fx = [];
  let next: ReaderModel = { ...m, lineCursor: at };
  if (block && m.folded.has(block)) fx.push({ kind: 'set-fold', key: block, value: false });
  const change = m.changes.findIndex((c) => c.key === block);
  if (change >= 0 && change !== m.cursor) {
    next = { ...next, cursor: change };
    fx.push({ kind: 'cursor', key: block! });
  }
  const i = stopIndex(m.stops as LineStop[], at);
  if (m.rangeAnchor && i >= 0) {
    const end = rangeEndAt(m.stops as LineStop[], m.rangeAnchor, i);
    if (end !== null) next = { ...next, selection: { side: m.rangeAnchor.side, start: m.rangeAnchor.line, end }, selected: null };
  }
  fx.push({ kind: 'scroll-to', to: { kind: 'line', at }, how: 'top' }, { kind: 'focus', what: 'reader' }, announce(said));
  return [next, fx];
}

function moveScope(m: ReaderModel, command: Extract<Command, { kind: 'move' }>): Step {
  if (!m.file) return [m, [NO_FILE]];
  if (m.preview) return [m, [PREVIEW_ONLY]];
  const waiting = (s: Scope) => m.changes.some((c) => !c.reviewed && !!c.lines.RIGHT && c.lines.RIGHT.start <= s.endLine && s.startLine <= c.lines.RIGHT.end);
  // A scope is somewhere to go while its head line is shown in the diff.
  const heads = [...m.scopes.RIGHT].filter((s) => m.blockOf.has(anchorKey('RIGHT', s.headLine)) && (!command.only || waiting(s))).sort((a, b) => a.headLine - b.headLine);
  if (!heads.length) return [m, [nothingFor(command, HERE)]];
  const line = m.newLine ?? (command.dir > 0 ? 0 : Infinity);
  const ahead = command.dir > 0 ? heads.filter((s) => s.headLine > line) : heads.filter((s) => s.headLine < line).reverse();
  if (!ahead.length) return [m, [notApplicable(`No ${command.only ? 'unreviewed ' : ''}scope ${command.dir > 0 ? 'after' : 'before'} this line`)]];
  const to = ahead[Math.min(times(command), ahead.length) - 1];
  return landOnLine(m, { side: 'RIGHT', line: to.headLine }, `${to.title}, line ${to.headLine}`);
}

// ---------------------------------------------------------------- files

function moveFile(m: ReaderModel, command: Extract<Command, { kind: 'move' }>): Step {
  const order = command.only ? m.files.filter((f) => f.path === m.file || !skipsFile(f.progress)).map((f) => f.path) : [...m.visible];
  const at = m.file ? order.indexOf(m.file) : -1;
  const ahead = command.dir > 0 ? order.slice(at + 1) : at < 0 ? [] : order.slice(0, at).reverse();
  if (!ahead.length) return [m, [notApplicable(`No ${command.only ? 'unreviewed ' : ''}file ${command.dir > 0 ? 'after' : 'before'} this one`)]];
  const path = ahead[Math.min(times(command), ahead.length) - 1];
  return [m, [{ kind: 'open-file', path }, { kind: 'focus', what: 'reader' }, announce(path)]];
}

// ---------------------------------------------------------------- threads

const threadsOf = (m: ReaderModel, only: Only | undefined) => (only ? m.threads.filter((t) => !t.resolved) : m.threads);

function landOnThread(m: ReaderModel, id: string, only: Only | undefined): Step {
  const list = threadsOf(m, only);
  const i = list.findIndex((t) => t.id === id);
  const block = list[i]?.block ?? null;
  const fx: Fx = [];
  if (block) fx.push({ kind: 'set-fold', key: block, value: false });
  fx.push({ kind: 'scroll-to', to: { kind: 'thread', id }, how: 'top' }, announce(`${only ? 'Unresolved thread' : 'Thread'} ${i + 1} of ${list.length}`));
  return [{ ...m, threadCursor: id }, fx];
}

function moveThread(m: ReaderModel, dir: Dir, only: Only | undefined, count: number): Step {
  if (!m.file) return [m, [NO_FILE]];
  const list = threadsOf(m, only);
  const at = m.threadCursor ? list.findIndex((t) => t.id === m.threadCursor) : -1;
  // Without a thread to continue from, the motion starts from what the reader shows.
  if (at < 0) return [m, [{ kind: 'seek', seek: { object: 'thread', dir, only, count, from: 'view' } }]];
  const ahead = dir > 0 ? list.slice(at + 1) : list.slice(0, at).reverse();
  if (ahead.length >= count) return landOnThread(m, ahead[count - 1].id, only);
  return [m, [{ kind: 'seek', seek: { object: 'thread', dir, only, count: count - ahead.length, from: 'file' } }]];
}

// ---------------------------------------------------------------- selecting

/** Lines `span` of `side` selected as a range: the range starts at its first line and the line cursor rests on its last. */
function selectLines(m: ReaderModel, side: Side, span: LineSpan, note: string, o: { keepStack?: boolean; selected?: Ref } = {}): Step {
  const n = span.end - span.start + 1;
  const next: ReaderModel = {
    ...m,
    selection: { side, start: span.start, end: span.end },
    selected: o.selected ?? null,
    rangeAnchor: { side, line: span.start },
    lineCursor: { side, line: span.end },
    grown: { stack: o.keepStack ? m.grown.stack : [], to: { side, span } },
  };
  return [next, [announce(`${note}: ${sideName(side)} ${n === 1 ? `line ${span.start}` : `lines ${span.start} to ${span.end}`}. A verb now acts on them.`)]];
}

/** The change around the line cursor, else the cursor's. */
function changeAtCursor(m: ReaderModel): ChangeInfo | null {
  const block = m.lineCursor ? m.blockOf.get(anchorKey(m.lineCursor.side, m.lineCursor.line)) : undefined;
  return m.changes.find((c) => c.key === block) ?? m.changes[m.cursor] ?? null;
}

function select(m: ReaderModel, command: Extract<Command, { kind: 'select' }>): Step {
  if (!m.file) return [m, [NO_FILE]];
  if (command.object === 'file') return [{ ...cleared(m), selected: { object: 'file', id: m.file } }, [announce(`${m.file} selected. A verb now acts on the file.`)]];
  if (m.preview) return [m, [PREVIEW_ONLY]];
  switch (command.object) {
    case 'line': {
      const at = m.lineCursor ?? (m.stops.length ? m.stops[lineStopAfter(m.stops as LineStop[], null, m.changes[m.cursor]?.key ?? null, 0, m.blocks)] : null);
      if (!at) return [m, [nothingFor(command, HERE)]];
      return selectLines(m, at.side, { start: at.line, end: at.line }, 'Line selected');
    }
    case 'change': {
      const c = changeAtCursor(m);
      if (!c) return [m, [nothingFor(command, HERE)]];
      const want = m.lineCursor?.side ?? 'RIGHT';
      const side: Side = c.lines[want] ? want : want === 'RIGHT' ? 'LEFT' : 'RIGHT';
      const span = c.lines[side];
      if (!span) return [m, [nothingFor(command, HERE)]];
      const [next, fx] = selectLines({ ...m, cursor: m.changes.indexOf(c) }, side, span, 'Change selected', { selected: { object: 'change', id: c.key } });
      return [next, m.changes[m.cursor] === c ? fx : [{ kind: 'cursor', key: c.key }, ...fx]];
    }
    case 'scope': {
      const s = command.at ? (foldable(m.scopes.RIGHT).find((x) => scopeId(x) === command.at!.id) ?? null) : m.newLine === null ? null : scopeUnder(m.scopes.RIGHT, m.newLine);
      if (!s) return [m, [notApplicable('No function, module or class around this line')]];
      return selectLines(m, 'RIGHT', { start: s.startLine, end: s.endLine }, `${s.title} selected`);
    }
    case 'draft':
    case 'thread':
    case 'pr':
    case 'group':
      return [m, [nothingFor(command, HERE)]];
  }
}

/** Start a range at the line cursor, or clear the one there is. */
function range(m: ReaderModel): Step {
  if (!m.file) return [m, [NO_FILE]];
  if (m.preview) return [m, [PREVIEW_ONLY]];
  if (m.rangeAnchor) return [cleared(m), [announce('Range cleared')]];
  let next = m;
  const fx: Fx = [];
  if (!next.lineCursor) {
    if (!m.stops.length) return [m, [notApplicable('No line to start a range at')]];
    const [placed, placing] = landOnStop(m, lineStopAfter(m.stops as LineStop[], null, m.changes[m.cursor]?.key ?? null, 0, m.blocks));
    next = placed;
    fx.push(...placing);
  }
  const at = next.lineCursor!;
  next = { ...next, rangeAnchor: { ...at }, selection: { side: at.side, start: at.line, end: at.line }, selected: null };
  fx.push(announce(`Range started at ${sideName(at.side)} line ${at.line}. Move the line cursor to extend it; a verb then acts on it.`));
  return [next, fx];
}

/** Grow the selection (or the cursor line) to the change around it, then each scope around that; or shrink back a step. */
function grow(m: ReaderModel, dir: Dir): Step {
  if (!m.file) return [m, [NO_FILE]];
  if (m.preview) return [m, [PREVIEW_ONLY]];
  const current: Grown | null = m.selection
    ? { side: m.selection.side, span: { start: Math.min(m.selection.start, m.selection.end), end: Math.max(m.selection.start, m.selection.end) } }
    : m.lineCursor
      ? { side: m.lineCursor.side, span: { start: m.lineCursor.line, end: m.lineCursor.line } }
      : null;
  const same = (a: Grown | null, b: Grown) => !!a && a.side === b.side && a.span.start === b.span.start && a.span.end === b.span.end;
  // The steps back belong to the selection they led to: any other selection starts anew.
  const stack = current && same(m.grown.to, current) ? m.grown.stack : [];
  if (dir < 0) {
    const back = stack[stack.length - 1];
    if (!back) return [{ ...m, grown: { stack: [], to: m.grown.to } }, [notApplicable('Nothing to shrink the selection back to')]];
    return selectLines({ ...m, grown: { stack: stack.slice(0, -1), to: m.grown.to } }, back.side, back.span, 'Selection shrunk', { keepStack: true });
  }
  if (!current) return [m, [notApplicable('Put the line cursor on a line, or select lines, first', { kind: 'move', object: 'line', dir: 1 })]];
  const { side, span } = current;
  const scopes = m.scopes[side];
  const block = m.blockOf.get(anchorKey(side, span.start));
  const change = m.changes.find((c) => c.key === block)?.lines[side] ?? null;
  const next = expandSelection(span, scopes, change);
  if (!next) return [m, [notApplicable('No enclosing change or scope to grow the selection to')]];
  const named = scopes.find((s) => s.startLine === next.start && s.endLine === next.end);
  return selectLines({ ...m, grown: { stack: [...stack, current], to: m.grown.to } }, side, next, named ? `Selected ${named.title}` : 'Selected the change', { keepStack: true });
}

// ---------------------------------------------------------------- verbs

/**
 * What a marking verb acts on: the object named outright, the object
 * selected whole, the selected lines, and only with nothing selected what
 * the cursor is on: the collapsed fold whose row the line cursor rests on,
 * else the cursor's change. Lines are taken for what they stand for
 * (`standsFor`), so a fold's row brings the lines the fold hides. Selected
 * lines with no changed line among them are `nothing`: a verb never
 * reaches past the selection to the cursor.
 */
type Target = { kind: 'file' } | { kind: 'change'; change: ChangeInfo } | { kind: 'range'; range: RangeInfo; fold: FoldInfo | null } | { kind: 'nothing' } | null;

/** The lines a verb acts on with nothing selected whole: the selection's, else the fold's whose row the line cursor is on. With `fold`, they are that one fold's. */
function linesTarget(m: ReaderModel): { range: RangeInfo | null; fold: FoldInfo | null } | null {
  if (m.selection) return { range: standsFor(m, m.selection), fold: null };
  const fold = foldAt(m, m.lineCursor);
  return fold ? { range: standsFor(m, { side: 'RIGHT', start: fold.head, end: fold.head }), fold } : null;
}

function markTarget(m: ReaderModel, at: Ref | undefined): Target {
  const ref = at ?? selectedObject(m);
  if (ref?.object === 'file') return { kind: 'file' };
  if (ref?.object === 'change') {
    const change = m.changes.find((c) => c.key === ref.id);
    return change ? { kind: 'change', change } : null;
  }
  const lines = at ? null : linesTarget(m);
  if (lines) return lines.range?.marks.length ? { kind: 'range', range: lines.range, fold: lines.fold ?? (lines.range.folds.length === 1 && coversOnly(m, lines.range, lines.range.folds[0]) ? lines.range.folds[0] : null) } : { kind: 'nothing' };
  const change = m.changes[m.cursor];
  return change ? { kind: 'change', change } : null;
}

/** Whether every changed line `range` reaches is one `fold` hides (or its own row's): the range is then the fold's, and is named so. */
function coversOnly(m: ReaderModel, range: RangeInfo, fold: FoldInfo): boolean {
  return foldReview(m.rows, fold).total === range.marks.length;
}

function mark(m: ReaderModel, command: Extract<Command, { kind: 'act' }>): Step {
  if (!m.file) return [m, [NO_FILE]];
  if (m.marksOff !== null) return [m, [notApplicable(m.marksOff)]];
  const value = command.verb === 'review';
  const target = markTarget(m, command.at);
  if (!target) return [m, [nothingFor(command, HERE)]];
  const already = (what: string) => notApplicable(value ? `${what} is reviewed already` : `${what} has no review mark`, value ? { kind: 'act', verb: 'unmark' } : { kind: 'act', verb: 'review' });
  const onward: Command | null = command.advance && value ? { kind: 'move', object: 'change', dir: 1, only: 'unreviewed' } : null;
  switch (target.kind) {
    case 'nothing':
      return [m, [notApplicable('No changed line in the selection to mark')]];
    case 'file': {
      if (!m.changes.length) return [m, [nothingFor(command, HERE)]];
      const done = m.changes.every((c) => c.reviewed);
      if (value ? done : m.changes.every((c) => c.covered === 0)) return [m, [already('The file')]];
      return [cleared(m), [{ kind: 'write-marks', target: { kind: 'file' }, value, then: onward }, { kind: 'focus', what: 'reader' }, announce(value ? 'File marked reviewed' : 'Review marks removed from the file')]];
    }
    case 'change': {
      const c = target.change;
      const name = `Change ${m.changes.indexOf(c) + 1} of ${m.changes.length}`;
      if (!c.covered && !c.uncovered.length) return onward ? step(m, onward) : [m, [notApplicable(`${name} has no changed line to review`)]];
      if (value ? c.reviewed : c.covered === 0) {
        if (onward) return step(m, onward);
        // Reviewed already, with more to review: what the reader wants is the next unreviewed change, not this one's mark removed.
        const more = value && (reviewedFlags(m).some((r) => !r) || others(m).some((p) => !skipsFile(p)));
        return [m, [more ? notApplicable(`${name} is reviewed already`, { kind: 'move', object: 'change', dir: 1, only: 'unreviewed' }) : already(name)]];
      }
      const next = selectedObject(m)?.object === 'change' ? cleared(m) : m;
      const fx: Fx = [{ kind: 'write-marks', target: { kind: 'change', key: c.key }, value, then: onward }];
      if (!onward) fx.push({ kind: 'focus', what: 'reader' }, announce(value ? 'Change marked reviewed' : 'Review mark removed'));
      return [next, fx];
    }
    case 'range': {
      const r = target.range;
      if (!m.marksReady) return [m, [notApplicable('Review progress is still loading')]];
      if (value ? r.covered === r.marks.length : r.covered === 0) return [m, [already(target.fold ? `The folded ${target.fold.title}` : 'The selection')]];
      // Counted by what the write changes: the lines left to mark (or marked, for an unmark), and the changes they are in.
      const lines = value ? r.marks.length - r.covered : r.covered;
      const changes = r.spans.filter((sp) => (value ? sp.unmarked : sp.marked) > 0).length;
      const then: ReaderMsg = { kind: 'marked', value, lines, changes, fold: target.fold?.title ?? null, spans: r.spans.map(({ block, end }) => ({ block, end })), onward: !!onward };
      return [m, [{ kind: 'write-marks', target: { kind: 'lines', lines: r.marks }, value, then }]];
    }
  }
}

/** After a range was marked: the blocks it finished fold, and the line cursor goes on to the next line to review. */
function marked(m: ReaderModel, msg: Extract<ReaderMsg, { kind: 'marked' }>): Step {
  let next = cleared(m);
  const fx: Fx = [];
  const keys = msg.spans.map((s) => s.block);
  const promoted = msg.value ? m.changes.filter((c) => keys.includes(c.key) && c.reviewed) : [];
  for (const c of promoted) fx.push({ kind: 'set-fold', key: c.key, value: true });
  // Marked and moving on: the next unreviewed change, as the motion finds it, past everything that was marked.
  if (msg.onward) {
    const reached = m.changes.findIndex((c) => c.key === keys[keys.length - 1]);
    const [onward, more] = moveChange({ ...next, cursor: Math.max(next.cursor, reached) }, 1, 'unreviewed', 1);
    return [onward, [...fx, ...more]];
  }
  // Through a fold, what was marked is out of sight: it is said by change.
  const counted = msg.fold ? `${nChanges(msg.changes)} (${nLines(msg.lines)}) in the folded ${msg.fold}` : nLines(msg.lines);
  let text: string;
  if (!msg.value) text = `Unmarked ${counted}`;
  else if (promoted.length) {
    const [after, more, said] = afterPromotion(next, promoted[promoted.length - 1]);
    next = after;
    fx.push(...more);
    text = `${msg.fold ? `Marked ${counted} reviewed` : reviewedNote(promoted.map((c) => m.changes.indexOf(c) + 1))}. ${said}`;
  } else {
    // The line cursor goes on to the next line to review in the block.
    const last = msg.spans[msg.spans.length - 1];
    const c = m.changes.find((x) => x.key === last?.block);
    const line = c ? (c.uncovered.find((u) => u.row >= last.end) ?? c.uncovered[0])?.at : undefined;
    if (line) {
      const { at } = restAt(m, line);
      next = { ...next, lineCursor: at };
      fx.push({ kind: 'scroll-to', to: { kind: 'line', at }, how: 'nearest', withStub: true });
    }
    text = `Marked ${counted} reviewed`;
  }
  fx.push({ kind: 'focus', what: 'reader' }, announce(text));
  return [next, fx];
}

/**
 * After marking finished change `c` (it folds), the line cursor moves on
 * within the file: to the first unreviewed line of the next change with
 * any, which becomes the cursor's. With none, the line cursor is cleared.
 */
function afterPromotion(m: ReaderModel, c: ChangeInfo): [ReaderModel, Fx, string] {
  const i = changesAhead(m, m.changes.indexOf(c), 1, 'unreviewed')[0];
  const to = i === undefined ? null : m.changes[i];
  const line = to?.uncovered[0]?.at ?? null;
  const { at, fold } = line ? restAt(m, line) : { at: null, fold: null };
  if (!to || !at) {
    const elsewhere = others(m).some((p) => !skipsFile(p)) || reviewedFlags(m).some((r) => !r);
    return [{ ...m, lineCursor: null }, [], elsewhere ? 'No unreviewed line after it in this file; the next unreviewed change is further on' : queueEnd(reviewedFlags(m), others(m))];
  }
  return [
    { ...m, cursor: i, lineCursor: at },
    [{ kind: 'cursor', key: to.key }, { kind: 'set-fold', key: to.key, value: false }, { kind: 'scroll-to', to: { kind: 'line', at }, how: 'nearest' }],
    fold ? `Line cursor on the folded ${fold.title}, change ${i! + 1}` : `Line cursor on ${sideName(at.side)} line ${at.line}, change ${i! + 1}`,
  ];
}

/**
 * Where a comment (or a link) goes: the selected lines, the cursor line,
 * else the cursor change's first changed line. The selection and the
 * cursor line are taken for what they stand for: a fold's row brings the
 * lines the fold hides (`folds` are those reached into).
 */
function linesAtCursor(m: ReaderModel): (Lines & { folds: readonly FoldInfo[] }) | null {
  const stood = (lines: Lines) => {
    const r = standsFor(m, lines);
    return { ...(r?.through ?? lines), folds: r?.folds ?? [] };
  };
  if (m.selection) return stood(m.selection);
  if (m.lineCursor && stopIndex(m.stops as LineStop[], m.lineCursor) >= 0) return stood({ side: m.lineCursor.side, start: m.lineCursor.line, end: m.lineCursor.line });
  const at = m.changes[m.cursor]?.commentAt;
  return at ? { side: at.side, start: at.line, end: at.line, folds: [] } : null;
}

/** A comment box on `lines`: a fold they reach into is opened first, so the lines written about, and the box under them, are seen. */
function composerOn(lines: Lines, folds: readonly FoldInfo[], suggest: boolean): Fx {
  const open: ReaderEffect = { kind: 'open-composer', side: lines.side, from: lines.start, to: lines.end, suggest };
  return folds.length ? [{ kind: 'fold-scopes', ids: folds.map((f) => f.id), value: false }, open] : [open];
}

function act(m: ReaderModel, command: Extract<Command, { kind: 'act' }>): Step {
  switch (command.verb) {
    case 'review':
    case 'unmark':
      return mark(m, command);
    case 'comment':
    case 'edit': {
      if (!m.file) return [m, [NO_FILE]];
      if (m.preview) return [m, [PREVIEW_ONLY]];
      if (selectedObject(m)?.object === 'file') return [m, [notApplicable('A comment goes on lines: select the lines it is about')]];
      const at = linesAtCursor(m);
      if (!at) return [m, [nothingFor(command, HERE)]];
      // Writes fail here: say why instead of taking a comment that cannot be saved.
      if (m.readOnly) return [cleared(m), [{ kind: 'read-only' }]];
      return [{ ...m, rangeAnchor: null, selected: null }, composerOn(at, at.folds, command.verb === 'edit')];
    }
    case 'copy': {
      if (!m.file) return [m, [NO_FILE]];
      const at = selectedObject(m)?.object === 'file' || m.preview ? null : linesAtCursor(m);
      return [m, [{ kind: 'copy-link', lines: at && { side: at.side, start: at.start, end: at.end } }]];
    }
    case 'discard':
    case 'include':
      return [m, [nothingFor(command, 'in Files', { kind: 'goto', place: 'review' })]];
    case 'open':
      return [m, [nothingFor(command, 'in Files')]];
  }
}

// ---------------------------------------------------------------- folds and presentation

function foldScopes(m: ReaderModel, scopes: readonly Scope[], value: boolean, said: string): Step {
  let next = m;
  // The line cursor never rests inside a folded body: it goes to the fold's head line.
  if (value && m.lineCursor && m.newLine !== null) {
    const around = scopes.find((s) => s.headLine < m.newLine! && m.newLine! <= s.endLine);
    if (around) next = { ...m, lineCursor: { side: 'RIGHT', line: around.headLine } };
  }
  return [next, [{ kind: 'fold-scopes', ids: scopes.map(scopeId), value }, announce(said)]];
}

function fold(m: ReaderModel, command: Extract<Command, { kind: 'fold' }>): Step {
  if (!m.file) return [m, [NO_FILE]];
  const object = command.at?.object ?? command.object;
  if (object === 'change') {
    const c = command.at ? m.changes.find((x) => x.key === command.at!.id) : m.changes[m.cursor];
    const key = c?.key ?? (command.at && m.blocks.includes(command.at.id) ? command.at.id : null);
    if (!key) return [m, [nothingFor(command, HERE)]];
    const folded = m.folded.has(key);
    const value = command.to === 'toggle' ? !folded : command.to === 'closed';
    return [m, value === folded ? [] : [{ kind: 'set-fold', key, value }]];
  }
  if (object && object !== 'scope') return [m, [nothingFor(command, HERE)]];
  if (m.preview) return [m, [PREVIEW_ONLY]];
  const scopes = m.scopes.RIGHT;
  const isFolded = (s: Scope) => m.foldedScopes.has(scopeId(s));
  if (command.at) {
    const s = foldable(scopes).find((x) => scopeId(x) === command.at!.id);
    if (!s) return [m, [nothingFor(command, HERE)]];
    const value = command.to === 'toggle' ? !isFolded(s) : command.to === 'closed';
    return isFolded(s) === value ? [m, []] : foldScopes(m, [s], value, `${value ? 'Folded' : 'Unfolded'} ${s.title}`);
  }
  if (!scopes.length) return [m, [notApplicable('No scopes to fold in this file')]];
  const around = m.newLine === null ? null : scopeUnder(scopes, m.newLine);
  if (command.level) {
    const level = levelScopes(scopes, around?.depth ?? 0);
    const value = command.to === 'toggle' ? !level.every(isFolded) : command.to === 'closed';
    return foldScopes(m, level, value, `${value ? 'Folded' : 'Unfolded'} ${level.length} scope${level.length === 1 ? '' : 's'} at this depth`);
  }
  if (!around) return [m, [notApplicable('No function, module or class around this line')]];
  if (command.to === 'toggle') return foldScopes(m, [around], !isFolded(around), `${isFolded(around) ? 'Unfolded' : 'Folded'} ${around.title}`);
  const value = command.to === 'closed';
  // Opening: a fold whose head line the cursor rests on is the one meant, before the scope around that line.
  const target = value ? around : (foldable(scopes).find((s) => isFolded(s) && s.headLine === m.newLine) ?? around);
  return isFolded(target) === value ? [m, []] : foldScopes(m, [target], value, `${value ? 'Folded' : 'Unfolded'} ${target.title}`);
}

function present(m: ReaderModel, command: Extract<Command, { kind: 'present' }>): Step {
  switch (command.what) {
    case 'centre': {
      if (m.lineCursor && !m.preview) return [m, [{ kind: 'scroll-to', to: { kind: 'line', at: m.lineCursor }, how: 'centre' }]];
      const c = m.changes[m.cursor];
      return c && !m.preview ? [m, [{ kind: 'scroll-to', to: { kind: 'change', key: c.key }, how: 'centre' }]] : [m, [nothingFor(command, HERE)]];
    }
    case 'preview':
      return m.previewable ? [m, [{ kind: 'set-preview', on: !m.preview }]] : [m, [notApplicable('This file has no rendered preview')]];
    case 'file-list':
      return [m, [{ kind: 'set-file-list', hidden: !m.fileListHidden }]];
    case 'wrap':
      return [m, [{ kind: 'set-pref', pref: 'wrap', value: !m.wrap }, announce(m.wrap ? 'Long lines scroll' : 'Long lines wrap')]];
    case 'layout': {
      const value = m.layout === 'split' ? 'unified' : 'split';
      return [m, [{ kind: 'set-pref', pref: 'layout', value }, announce(value === 'split' ? 'Split diff' : 'Unified diff')]];
    }
    case 'algorithm': {
      if (!m.algorithms.length) return [m, [nothingFor(command, HERE)]];
      const value = m.algorithms[(m.algorithms.indexOf(m.algorithm) + 1) % m.algorithms.length];
      return [m, [{ kind: 'set-pref', pref: 'algorithm', value }]];
    }
  }
}

// ---------------------------------------------------------------- update

function move(m: ReaderModel, command: Extract<Command, { kind: 'move' }>): Step {
  switch (command.object) {
    case 'change':
      return moveChange(m, command.dir, command.only, times(command));
    case 'line':
      return moveLine(m, command);
    case 'scope':
      return moveScope(m, command);
    case 'file':
      return moveFile(m, command);
    case 'thread':
      return moveThread(m, command.dir, command.only, times(command));
    case 'draft':
      return [m, [nothingFor(command, 'in Files', { kind: 'goto', place: 'review' })]];
    case 'pr':
      // The stack is the page's.
      return [m, [pass]];
    case 'group':
      return [m, [nothingFor(command, 'in Files')]];
  }
}

/** How far one scroll step goes, in pixels: down or up, and sideways. */
export const SCROLL_STEP = { y: 60, x: 80 } as const;

const POINTER: ReadonlySet<string> = new Set(['file-opened', 'comparison-changed', 'gutter-pressed', 'drag-moved', 'drag-ended', 'drag-abandoned', 'lines-shown', 'selection-dropped', 'view-moved']);
const isPointer = (msg: ReaderMsg): msg is PointerMsg => POINTER.has(msg.kind);

function pointer(state: ReaderState, env: ReaderEnv, msg: PointerMsg): Update<ReaderState, ReaderEffect> {
  switch (msg.kind) {
    case 'file-opened':
      return [READER_START, []];
    case 'comparison-changed':
      return [{ ...state, selection: null, selected: null, dragging: false, lineCursor: null, rangeAnchor: null }, []];
    case 'gutter-pressed': {
      const path = env.file ?? '';
      const next = gutterPress(state.selection && { path, ...state.selection, dragging: state.dragging }, { path, side: msg.at.side, line: msg.at.line, shift: msg.shift });
      const selection = next.selection && { side: next.selection.side, start: next.selection.start, end: next.selection.end };
      return [{ ...state, lineCursor: msg.at, rangeAnchor: null, selection, selected: null, dragging: next.dragging }, []];
    }
    case 'drag-moved':
      return state.dragging && state.selection && state.selection.end !== msg.line ? [{ ...state, selection: { ...state.selection, end: msg.line } }, []] : [state, []];
    case 'drag-abandoned':
      return [state.dragging ? { ...state, dragging: false } : state, []];
    case 'drag-ended': {
      const s = state.selection;
      if (!state.dragging || !s) return [state, []];
      // The lines pressed, for what they stand for: a fold's row brings the lines the fold hides.
      const stood = standsFor(env, s);
      const { start: lo, end: hi } = stood?.through ?? { start: Math.min(s.start, s.end), end: Math.max(s.start, s.end) };
      if (s.start === s.end && !msg.shift) return [{ ...state, dragging: false }, composerOn({ side: s.side, start: lo, end: hi }, stood?.folds ?? [], false)];
      // A range as the range command makes one: a verb acts on it, and moving the line cursor extends it.
      const lines = lo === hi ? `${sideName(s.side)} line ${lo}` : `${sideName(s.side)} lines ${lo} to ${hi}`;
      const through = stood?.folds.length ? `, through the folded ${stood.folds.map((f) => f.title).join(' and ')}` : '';
      return [
        { ...state, dragging: false, rangeAnchor: { side: s.side, line: s.start }, lineCursor: { side: s.side, line: s.end } },
        [{ kind: 'focus', what: 'reader' }, announce(`Selected ${lines}${through}. A verb now acts on them.`)],
      ];
    }
    case 'lines-shown':
      return [{ ...state, selection: msg.lines, selected: null, dragging: false }, []];
    case 'selection-dropped':
      return [{ ...state, selection: null, selected: null, dragging: false, ...(msg.keepRange ? {} : { rangeAnchor: null }) }, []];
    case 'view-moved': {
      const i = env.changes.findIndex((c) => c.key === msg.key);
      // Lines chosen, or a comment being written on them, are what a verb acts on wherever the reader looks: the cursor waits for them.
      if (i < 0 || i === env.cursor || state.selection || state.rangeAnchor || state.dragging || env.composing) return [state, []];
      // The line cursor stays only if it is in the change the reader came to.
      const at = state.lineCursor;
      const kept = at && env.blockOf.get(anchorKey(at.side, at.line)) === msg.key ? at : null;
      const said = `Change ${i + 1} of ${env.changes.length}${env.changes[i].reviewed ? ', reviewed' : ', not reviewed'}`;
      return [kept === at ? state : { ...state, lineCursor: kept }, [{ kind: 'cursor', key: msg.key }, announce(said)]];
    }
  }
}

const OWNED = ['lineCursor', 'rangeAnchor', 'selection', 'selected', 'threadCursor', 'grown'] as const;

/**
 * The reader's update. The state that comes back is the reader's model;
 * the same object comes back when nothing of it changed.
 */
export function updateReader(state: ReaderState, env: ReaderEnv, msg: ReaderMsg): Update<ReaderState, ReaderEffect> {
  if (isPointer(msg)) return pointer(state, env, msg);
  // While a drag is still choosing its end, nothing is selected for a command.
  const { dragging, ...own } = state;
  const before: ReaderModel = { ...env, ...own, selection: dragging ? null : own.selection };
  const [after, effects] = step(before, msg);
  if (OWNED.every((k) => after[k] === before[k])) return [state, effects];
  // A command that selects, or drops the selection, ends a drag's say over it; one that only moves a cursor leaves the drag as it is.
  const reselected = after.selection !== before.selection || after.selected !== before.selected || after.rangeAnchor !== before.rangeAnchor;
  return [
    { lineCursor: after.lineCursor, rangeAnchor: after.rangeAnchor, selection: reselected ? after.selection : state.selection, dragging: reselected ? false : dragging, selected: after.selected, threadCursor: after.threadCursor, grown: after.grown },
    effects,
  ];
}

function step(m: ReaderModel, msg: Exclude<ReaderMsg, PointerMsg>): Step {
  switch (msg.kind) {
    case 'move':
      return move(m, msg);
    case 'scroll': {
      const n = msg.dir * times(msg);
      if (msg.by === 'half-page') return [m, [{ kind: 'scroll', dx: 0, dy: 0.5 * n, unit: 'page' }]];
      return [m, [msg.axis === 'y' ? { kind: 'scroll', dx: 0, dy: SCROLL_STEP.y * n, unit: 'px' } : { kind: 'scroll', dx: SCROLL_STEP.x * n, dy: 0, unit: 'px' }]];
    }
    case 'select':
      return select(m, msg);
    case 'range':
      return range(m);
    case 'grow': {
      let step: Step = grow(m, msg.dir);
      for (let n = times(msg) - 1; n > 0 && !step[1].some((e) => e.kind === 'not-applicable'); n--) {
        const [next, fx] = grow(step[0], msg.dir);
        // The last step that had somewhere to go is the one that stands.
        if (fx.some((e) => e.kind === 'not-applicable')) break;
        step = [next, fx];
      }
      return step;
    }
    case 'act':
      return act(m, msg);
    case 'goto':
      if (msg.place === 'top' || msg.place === 'end') return [m, [{ kind: 'scroll-to', to: { kind: msg.place }, how: 'top' }]];
      return [m, [pass]];
    case 'fold':
      return fold(m, msg);
    case 'present':
      return present(m, msg);
    case 'open':
      return [m, [msg.menu === 'finder' ? { kind: 'focus', what: 'finder' } : pass]];
    case 'cancel':
      return [cleared(m), [{ kind: 'leave-bar' }, { kind: 'close-composer' }]];
    case 'refresh':
    case 'submit':
      return [m, [pass]];

    case 'arrived': {
      const i = m.changes.findIndex((c) => c.key === msg.key);
      if (i < 0) return [m, []];
      const note = msg.wrapped ? `Wrapped to the ${msg.dir > 0 ? 'first' : 'last'} ${msg.wrapped}` : null;
      const [next, fx] = landOnChange(m, i, msg.only, (where) => `${note ? `${note}. ` : ''}${m.file}, change ${i + 1} of ${m.changes.length}${where ? `. ${where}` : ''}`);
      // Shown beside the buttons for a while; announced too, so hidden from readers there.
      return [next, note ? [...fx, { kind: 'note', text: note, passing: true }] : fx];
    }
    case 'past-sections':
      return moveChange(m, msg.dir, msg.only, msg.count, false);
    case 'found-thread': {
      const at: ReaderModel = { ...m, threadCursor: msg.id };
      return msg.rest > 0 ? moveThread(at, msg.dir, msg.only, msg.rest) : landOnThread(at, msg.id, msg.only);
    }
    case 'entered': {
      const list = threadsOf(m, msg.only);
      if (!list.length) return [m, []];
      const i = Math.min(msg.count, list.length) - 1;
      return landOnThread(m, (msg.dir > 0 ? list[i] : list[list.length - 1 - i]).id, msg.only);
    }
    case 'nowhere': {
      const s = msg.seek;
      if (s.object === 'thread') return [m, [notApplicable(`No ${s.only ? 'unresolved ' : ''}thread ${s.dir > 0 ? 'after' : 'before'} this point`)]];
      if (!s.only) return [m, [notApplicable('No change to go to')]];
      const text = queueEnd(reviewedFlags(m), others(m));
      return [m, [{ kind: 'note', text }, announce(text)]];
    }
    case 'marked':
      return marked(m, msg);
    case 'scope-chosen': {
      if (!m.file) return [m, [NO_FILE]];
      if (m.preview) return [m, [PREVIEW_ONLY]];
      const at = { side: msg.side, line: msg.scope.headLine };
      if (!m.blockOf.has(anchorKey(at.side, at.line))) return [m, [notApplicable(`${msg.scope.title} starts on a line this diff does not show`)]];
      return landOnLine(m, at, `${msg.scope.title}, ${sideName(msg.side)} line ${at.line}`);
    }
  }
}
